import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';

import { filterParams } from '../utils/filters.js';

export function useTable(columns = [], queryObj = {}, options = {}) {
  // extra filters
  const [searchParams] = useSearchParams();
  const [selectColumnFilter, setSelectColumnFilter] = useState(
    options.selectColumnFilter || '',
  );

  // table variables
  const [columnVisibility, setColumnVisibility] = useState(
    options.columnVisibility || {},
  );
  const [columnFilters, setColumnFilters] = useState(
    options.columnFilters || [],
  );
  const [rowSelection, setRowSelection] = useState(options.rowSelection || {});
  const [sorting, setSorting] = useState(options.sorting || []);
  const [pagination, setPagination] = useState(
    options.pagination || {
      pageIndex: 0,
      pageSize: 100,
    },
  );

  // fetch data
  const query = useQuery({
    ...queryObj,
    queryKey: [[...queryObj.queryKey], pagination, columnFilters, sorting],
  });

  // build table
  const table = useReactTable({
    data: query?.data?.rows ?? [],
    columns: columns,
    rowCount: query?.data?.rowCount,
    state: {
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualFiltering: true,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: (val) => {
      setColumnFilters(val);
      if (val.length) setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableRowSelection: true,
    manualPagination: true,
    debugTable: false,
  });

  // set filters on page load from search params
  useEffect(() => {
    if (options?.searchQs) {
      const filters = [];

      searchParams.forEach((value, key) => {
        const column = columns.find((x) => x.accessorKey === key);
        const columnKey = column.id || column.accessorKey;

        filters.push({
          id: columnKey,
          value: value,
        });

        setSelectColumnFilter(columnKey);

        table.getColumn(columnKey).setFilterValue(value);
      });

      setColumnFilters(filters);
    }
  }, [searchParams]);

  return {
    table,
    query,
    filters: filterParams(table),
    sorting,
    pagination,
    columnFilters,
    selectColumnFilter,
    setSelectColumnFilter,
  };
}
